import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useBackend'
import { APIError } from '@/services/api'
import { AuthLayout } from './AuthLayout'

interface LocationState {
  from?: {
    pathname: string;
  };
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

export function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { login, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const state = location.state as LocationState
  const _from = state?.from?.pathname || '/pagina-inicial'

  const handleLogin = async () => {
    setEmailError('')
    setPasswordError('')
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      setEmailError('Informe seu email')
      return
    }
    if (!emailRegex.test(email)) {
      setEmailError('Informe um email válido')
      return
    }
    if (!password) {
      setPasswordError('Informe sua senha')
      return
    }

    try {
      setLoginError('')
      await login(email, password)
      sessionStorage.setItem('just_logged_in', 'true')
      // Convite pendente (link/QR da campanha): retoma o ingresso pós-login
      const pendingInvite = sessionStorage.getItem('pending_invite_code')
      navigate(pendingInvite ? `/convite/${pendingInvite}` : '/pagina-inicial', { replace: true })
    } catch (error) {
      if (error instanceof APIError) {
        if (error.status === 401) {
          setLoginError('Email ou senha incorretos')
        } else if (error.status === 0) {
          setLoginError('Não foi possível conectar ao servidor')
        } else if (error.message) {
          setLoginError(error.message)
        }
      } else {
        setLoginError('Não foi possível efetuar o login')
      }
    }
  }

  return (
    <AuthLayout>
      <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
        <div className="mb-6">
          <h2 className="text-xl font-bold">Bem-vindo(a) de volta</h2>
          <p className="mt-1 text-sm text-muted-foreground">Entre para continuar sua campanha.</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError('')
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLogin() }}
              autoFocus
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'email-error' : undefined}
            />
            {emailError && (
              <p id="email-error" className="mt-1 text-sm font-medium text-error" role="alert">
                {emailError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Senha</Label>
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent p-0 text-sm text-link hover:underline"
                onClick={() => navigate('/recuperar-senha')}
              >
                Esqueci minha senha
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (loginError) setLoginError('')
                  if (passwordError) setPasswordError('')
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin() }}
                autoComplete="current-password"
                aria-invalid={!!(loginError || passwordError)}
                className="pr-10"
              />
              <button
                type="button"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                className="absolute inset-y-0 right-2 flex items-center px-1 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(s => !s)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {passwordError && (
              <p className="mt-1 text-sm font-medium text-error" role="alert">
                {passwordError}
              </p>
            )}
            {loginError && (
              <p className="mt-1 text-sm font-medium text-error" role="alert">
                {loginError}
              </p>
            )}
          </div>

          <Button
            className="w-full"
            onClick={handleLogin}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>

          <div className="relative my-2">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              ou continue com
            </span>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full gap-3 font-semibold"
            onClick={() => { window.location.href = '/api/auth/google' }}
          >
            <GoogleIcon />
            Entrar com Google
          </Button>

          <p className="pt-1 text-center text-xs text-muted-foreground">
            Novo por aqui? Peça um código à sua turma.
          </p>
        </div>
      </div>
    </AuthLayout>
  )
}
